// COPIED from ptc-core web/src/telemetry/reportClientError.ts @ de39fff (fleet feedback contract v1, 2026-10-21).
// Changes from the source: none. In statetax VITE_CLIENT_ERROR_ENDPOINT is unset, so nothing is beaconed: the
// module keeps only the local ring that feedbackContext reads. Conformance: src/components/feedback/FeedbackWidgetConformance.test.tsx.

/**
 * Client-side error reporting for render-time crashes caught by RouteErrorBoundary.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠️  THIS PATH IS A DATA EGRESS POINT. IT MUST STAY PII-FREE.
 *
 * Everything assembled here leaves the browser and lands in a log store that is
 * queried by people and retained for months. Include ONLY what identifies the
 * *code* that broke — never what the user was looking at or typing.
 *
 *   ALLOWED : error name/message, stack, React component stack, route pathname,
 *             timestamp, user agent, build id (the commit that produced the bundle).
 *   BANNED  : form values, request/response bodies, field contents, SSN/EIN,
 *             contact or company names, email addresses, phone numbers,
 *             auth tokens, and `location.search` / `location.hash`.
 *
 * The query-string exclusion is deliberate and matches the reasoning already
 * recorded at `src/api/contacts.ts:101` — a search term like `?q=John+Smith`
 * would otherwise land in access logs and telemetry. We send `pathname` only.
 * Pathnames do carry record GUIDs (e.g. `/contacts/2f9c…`); those are opaque
 * identifiers, not personal data, and are needed to reproduce the crash.
 *
 * If you add a field here, re-read the two lists above first.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * TRANSPORT STATUS (read before wiring anything new)
 *
 * The web app has NO telemetry SDK. `web/package.json` contains no
 * `@microsoft/applicationinsights-web`, and there is no App Insights bootstrap
 * anywhere under `web/src`. Adding the SDK was not an option here, so this
 * module ships a zero-dependency `navigator.sendBeacon` transport instead.
 *
 * WIRED as of 2026-07-25. Option (a) below shipped: `POST v1/client-errors` on
 * `ptc-core-crm-api` (`src/PtcCore.Crm.Api/Endpoints/ClientErrorsFunction.cs`),
 * anonymous by necessity — `sendBeacon` cannot set an `Authorization` header —
 * and bounded to a size-capped, rate-limited, log-only write. Deployed builds
 * get the URL from `.github/workflows/crm-deploy.yml`, which sets
 * `VITE_CLIENT_ERROR_ENDPOINT` on BOTH the workflow `env:` and the "Build SPA"
 * step's own `env:`. Only the latter reaches Vite — a `VITE_*` var that is not
 * in the build step's environment is not inlined, and the bundle ships with the
 * transport silently inert.
 *
 * It stays INERT wherever `VITE_CLIENT_ERROR_ENDPOINT` is unset (local dev, the
 * test harness): reports are logged to the console and nothing is sent, which is
 * the pre-existing behaviour and a safe default.
 *
 * The rejected alternative was (b) posting to the App Insights REST track
 * endpoint directly; it would put an ingestion key in the JS bundle and let the
 * browser talk to a third origin, for no gain over our own Function App.
 */

/** The exact wire shape sent to the receiver. PII-free by construction — see header. */
export interface ClientErrorReport {
  /** Constructor name, e.g. "TypeError". */
  name: string;
  /** `error.message`. Truncated — see MAX_MESSAGE. */
  message: string;
  /** JS stack, truncated. May be minified in prod without source maps. */
  stack?: string;
  /** React's component stack from `componentDidCatch`'s ErrorInfo, truncated. */
  componentStack?: string;
  /** `location.pathname` ONLY — never search or hash. */
  pathname: string;
  /** ISO-8601 UTC. */
  timestamp: string;
  /** Browser/OS triage. */
  userAgent: string;
  /** Which boundary reported it, for when there is more than one. */
  source: string;
  /**
   * Which build produced this bundle: a 12-character commit SHA prefix, or
   * `'local'` for anything not built by the deploy workflow. See BUILD_ID.
   *
   * NOT a credential and never treated as one by the receiver — the endpoint is
   * anonymous and anything the SPA can send is in the public JS bundle. It is
   * signal hygiene: it makes a genuine crash from a known deploy separable from
   * arbitrary junk posted at an open endpoint, and it maps a minified stack back
   * to the source it came from.
   */
  buildId: string;
}

/**
 * Reads a Vite-inlined `VITE_*` value, returning `undefined` for every flavour of
 * "not configured".
 *
 * The blank-string case is the one that bites in CI: a workflow that writes
 * `VITE_FOO: ${{ env.VITE_FOO }}` against an undeclared workflow variable puts an
 * EMPTY STRING into the build environment, not an absent key. The `'undefined'` /
 * `'null'` cases are the other half of the same trap — a value that was
 * string-interpolated somewhere upstream while it was undefined arrives here as
 * those four/five literal characters, which are perfectly truthy and would be
 * used as a URL. (That exact stringification caused the statetax AADSTS900023
 * outage.) None of these are usable, so all of them mean "unset".
 *
 * Exported for tests: the module-level constants below are frozen at import time,
 * so the normalisation is asserted directly rather than through re-imports.
 */
export function readInlinedEnvVar(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  if (!trimmed || trimmed === 'undefined' || trimmed === 'null') return undefined;
  return trimmed;
}

const ENDPOINT = readInlinedEnvVar(import.meta.env.VITE_CLIENT_ERROR_ENDPOINT);

/**
 * Identifies the build, for triage. `crm-deploy.yml` passes the full 40-character
 * `github.sha`; we ship the first 12 on the wire.
 *
 * Twelve because git resolves any unambiguous prefix (`git show 1a2b3c4d5e6f`
 * works), 12 is far past the collision threshold for a repo this size, and it
 * stays readable inside a one-line log message — whereas the full 40 adds no
 * queryability, only width. `'local'` (never the string `'undefined'`) marks a
 * bundle that did not come from the deploy workflow: a dev server, a hand-run
 * `npm run build`, or the test harness.
 */
export const BUILD_ID: string =
  readInlinedEnvVar(import.meta.env.VITE_BUILD_SHA)?.slice(0, 12) ?? 'local';

const MAX_MESSAGE = 1_000;
const MAX_STACK = 4_000;
const MAX_COMPONENT_STACK = 4_000;

/**
 * Hard cap per page load. A render loop can trip a boundary continuously; without
 * this, reporting becomes the outage.
 */
const MAX_REPORTS_PER_SESSION = 5;
let reportsSent = 0;

/**
 * Re-entrancy guard. If anything inside this function throws and that throw is
 * itself caught by a boundary, we must not recurse. Set for the duration of the
 * call and cleared in `finally`.
 */
let reporting = false;

function truncate(value: string | undefined, max: number): string | undefined {
  if (!value) return undefined;
  return value.length > max ? `${value.slice(0, max)}…[truncated]` : value;
}

/**
 * The last few reports, kept in memory for the feedback dialog's "recent errors" context
 * (feedback-loop spec §3.2, BL-042: "do not build a second capture path"). It holds exactly the
 * {@link ClientErrorReport} objects assembled below, so it inherits that shape's PII rules: it
 * never holds anything the header's BANNED list forbids. Never persisted; gone on reload.
 */
const RECENT_LIMIT = 5;
let recent: ClientErrorReport[] = [];

/** Newest last. Copies, so a caller cannot mutate the ring. */
export function recentClientErrors(): ClientErrorReport[] {
  return recent.map((r) => ({ ...r }));
}

/** Exposed for tests; resets the per-session counter and the recent-errors ring. */
export function __resetClientErrorReporterForTests(): void {
  reportsSent = 0;
  reporting = false;
  recent = [];
}

/**
 * Fire-and-forget error report.
 *
 * CONTRACT: this function NEVER throws and NEVER returns a rejected promise. It is
 * called from `componentDidCatch`, on the path to rendering the fallback UI — an
 * exception here would replace a handled crash with an unhandled one. Every branch
 * is wrapped. Callers should still not `await` it: `sendBeacon` is non-blocking by
 * design and returns immediately.
 */
export function reportClientError(
  error: Error,
  context: { componentStack?: string; source?: string } = {}
): void {
  if (reporting) return; // reporting an error raised while reporting — drop it
  if (reportsSent >= MAX_REPORTS_PER_SESSION) return;

  reporting = true;
  try {
    reportsSent += 1;

    const report: ClientErrorReport = {
      name: error?.name ?? 'Error',
      message: truncate(error?.message, MAX_MESSAGE) ?? '(no message)',
      stack: truncate(error?.stack, MAX_STACK),
      componentStack: truncate(context.componentStack, MAX_COMPONENT_STACK),
      // pathname only — never location.search/hash. See the PII header.
      pathname: typeof window !== 'undefined' ? window.location.pathname : '(unknown)',
      timestamp: new Date().toISOString(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '(unknown)',
      source: context.source ?? 'RouteErrorBoundary',
      buildId: BUILD_ID
    };

    // Always leave a local trace, endpoint or not. This is what the boundary used
    // to do on its own and it stays useful in dev tools.
    console.error('[clientError]', report);

    // Endpoint or not, the feedback dialog can attach it (see recentClientErrors).
    recent = [...recent, report].slice(-RECENT_LIMIT);

    // Inert wherever VITE_CLIENT_ERROR_ENDPOINT is unset (local dev, tests). `readInlinedEnvVar`
    // has already collapsed ''/'undefined'/'null' to undefined, so a placeholder cannot be
    // mistaken for a configured URL and posted to.
    if (!ENDPOINT) return;

    const body = JSON.stringify(report);

    // sendBeacon: non-blocking, survives the page being torn down, and cannot
    // reject. Returns false if the payload was refused (too large / disabled).
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const ok = navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
      if (ok) return;
    }

    // Fallback for environments without sendBeacon. `keepalive` gives the request a
    // chance to finish if the user navigates away. The catch is mandatory: a
    // rejected fetch here would surface as an unhandled rejection.
    if (typeof fetch === 'function') {
      void fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true
      }).catch(() => {
        /* reporting is best-effort; a failed report must never surface to the user */
      });
    }
  } catch {
    // Swallow deliberately. A telemetry failure must never escalate into a second
    // crash on top of the one we are already handling.
  } finally {
    reporting = false;
  }
}
